import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { SelectField, TextAreaField, TextField } from '@/components/ui/Field';
import { FormMessage } from '@/components/ui/FormMessage';
import { QuestionThread } from '@/components/ui/QuestionThread';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { useInvalidFieldFocus } from '@/hooks/useInvalidFieldFocus';
import { ROLES } from '@/data/categories';
import { createId } from '@/lib/format';
import { isNonEmpty } from '@/lib/validation';
import type { FormFeedback, Question, Reply, Role } from '@/types';
import styles from './Qa.module.css';

export interface QaProps {
  readonly titleId: string;
  readonly questions: readonly Question[];
  readonly onAsk: (question: Question) => void;
  readonly onReply: (questionId: string, reply: Reply) => void;
}

interface Errors {
  author?: string;
  body?: string;
}

export function Qa({ titleId, questions, onAsk, onReply }: QaProps) {
  const [author, setAuthor] = useState('');
  const [role, setRole] = useState<Role>('معلم');
  const [body, setBody] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [feedback, setFeedback] = useState<FormFeedback | null>(null);
  const { formRef, focusFirstInvalid } = useInvalidFieldFocus();

  const submit = (event: FormEvent) => {
    event.preventDefault();

    const nextErrors: Errors = {};
    if (!isNonEmpty(author)) nextErrors.author = 'الرجاء إدخال اسمك.';
    if (!isNonEmpty(body)) nextErrors.body = 'الرجاء كتابة سؤالك.';

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setFeedback({ tone: 'error', message: 'الرجاء إدخال الاسم والسؤال قبل الإرسال.' });
      focusFirstInvalid();
      return;
    }

    onAsk({
      id: createId('question'),
      author: author.trim(),
      role,
      createdAt: new Date().toISOString(),
      body: body.trim(),
      replies: [],
    });

    setBody('');
    setFeedback({ tone: 'success', message: 'تم إرسال سؤالك بنجاح! سيتم الرد عليك قريباً.' });
  };

  const handleReply = (questionId: string, text: string) => {
    onReply(questionId, {
      id: createId('reply'),
      // Fall back to a neutral label when the visitor has not named themselves yet.
      author: author.trim() || 'مشارك',
      role,
      createdAt: new Date().toISOString(),
      body: text,
    });
  };

  return (
    <>
      <SectionHeader eyebrow="أسئلة وتفاعل" title="اطرح سؤالك هنا" titleId={titleId} />

      <div className={styles.layout}>
        <form ref={formRef} className={styles.form} onSubmit={submit} noValidate>
          <TextField
            label="الاسم"
            required
            value={author}
            onChange={(event) => setAuthor(event.target.value)}
            placeholder="أدخل اسمك"
            autoComplete="name"
            {...(errors.author ? { error: errors.author } : {})}
          />

          <SelectField
            label="الدور"
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
          >
            {ROLES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </SelectField>

          <TextAreaField
            label="السؤال"
            required
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder="اطرح سؤالك هنا..."
            rows={5}
            {...(errors.body ? { error: errors.body } : {})}
          />

          <Button type="submit">إرسال السؤال</Button>

          <FormMessage feedback={feedback} />
        </form>

        <div className={styles.thread}>
          <h3 className="visually-hidden">الأسئلة المطروحة</h3>
          {questions.map((question) => (
            <QuestionThread key={question.id} question={question} onReply={handleReply} />
          ))}
        </div>
      </div>
    </>
  );
}
